import { useMemo } from 'react';
import { SelectionRepository } from 'src/projects/SelectionRepository.ts';
import { readSearchParam } from 'src/common/readSearchParam.ts';
import { LOCATION_STORE_PARAM_NAME } from 'src/common/constants.ts';
import { initLocationState as initLocationStateValves } from 'src/modules/locationStateContext.ts';
import { initLocationState as initLocationStateSensors } from 'src/sensors/locationStateContext.ts';
import { initLocationState as initLocationStateActuators } from 'src/actuators/locationStateContext.ts';
import { Selection } from 'src/projects/types/Selection.ts';

// [name, mainLabel, qty, mainQty, actuatorLabel, productLabel].
// mainLabel    — the selection's valve / sensor / actuator; the connectors attribute is
//                read from it (a combo product has none), so it is set on every row.
// qty          — quantity of this row's product.
// mainQty      — 0 for the main product row; the main product's quantity for accessory rows
//                (the SQL applies it only when the accessory is a pipe connector).
// actuatorLabel — actuator of a valve combo, empty otherwise (price fallback).
// productLabel  — the product priced by this row (combo / main product / accessory); when it
//                has no own price the resolver falls back to mainLabel + actuatorLabel.
type MapRow = [string, string, string, string, string, string];

const getMapForSelection = (selection: Selection): MapRow[] => {
    switch (selection.type) {
        case 'valves': {
            const locationState = readSearchParam(
                new URLSearchParams(selection.searchQuery),
                LOCATION_STORE_PARAM_NAME,
                { ...initLocationStateValves },
                (v) => !!v,
            );
            const { combo } = locationState;
            const valveLabel = combo?.valve ?? locationState.valveLabel ?? '';
            const isCombo = !!combo?.actuator && combo.actuator !== combo.valve;
            const actuatorLabel = isCombo ? (combo?.actuator ?? '') : '';
            const comboLabel = isCombo ? `${combo?.valve}${combo?.mounted ? '+' : '/'}${combo?.actuator}` : valveLabel;
            const mainQty = locationState.pipeConnectors ? `${combo?.qty || 1}` : '1';
            return [
                [selection.name, valveLabel, mainQty, '0', actuatorLabel, comboLabel],
                ...locationState.selectedAccessories.map(({
                    qty,
                    label,
                }) => [selection.name, valveLabel, `${qty}`, mainQty, '', label] as MapRow),
            ];
        }
        case 'sensors': {
            const locationState = readSearchParam(
                new URLSearchParams(selection.searchQuery),
                LOCATION_STORE_PARAM_NAME,
                { ...initLocationStateSensors },
                (v) => !!v,
            );
            const mainQty = locationState.pipeConnectors ? `${locationState.qty}` : '1';
            return [
                [selection.name, locationState.sensorLabel, mainQty, '0', '', locationState.sensorLabel],
                ...locationState.selectedAccessories.map(({
                    qty,
                    label,
                }) => [selection.name, locationState.sensorLabel, `${qty}`, mainQty, '', label] as MapRow),
            ];
        }
        case 'actuators': {
            const locationState = readSearchParam(
                new URLSearchParams(selection.searchQuery),
                LOCATION_STORE_PARAM_NAME,
                { ...initLocationStateActuators },
                (v) => !!v,
            );
            const mainQty = `${locationState.qty}`;
            return [
                [selection.name, locationState.actuatorLabel, mainQty, '0', '', locationState.actuatorLabel],
                ...locationState.selectedAccessories.map(({
                    qty,
                    label,
                }) => [selection.name, locationState.actuatorLabel, `${qty}`, mainQty, '', label] as MapRow),
            ];
        }
        default: {
            return [
                [selection.name, selection.productLabel, `${selection.qty ?? 1}`, '0', '', selection.productLabel],
                ...(selection.selectedAccessories ?? []).map(({
                    qty,
                    label,
                }) => [
                    selection.name, selection.productLabel, `${qty}`, `${selection.qty ?? 1}`, '', label,
                ] as MapRow),
            ];
        }
    }
};

export const useGetCountsMap = (selectionRepository: SelectionRepository) => useMemo(
    () => selectionRepository
        .list(selectionRepository.count(), 0)
        .reduce((acc, selection) => {
            getMapForSelection(selection).forEach((item) => {
                acc.push(item);
            });
            return acc;
        }, [] as MapRow[]),
    [selectionRepository],
);
